import { isCohorteFull } from './cohorteCapacityMessage';

export const JOUR_SEMAINE_LABEL: Record<string, string> = {
  LUNDI: 'Lundi',
  MARDI: 'Mardi',
  MERCREDI: 'Mercredi',
  JEUDI: 'Jeudi',
  VENDREDI: 'Vendredi',
  SAMEDI: 'Samedi',
};

export const JOUR_ORDER = ['LUNDI', 'MARDI', 'MERCREDI', 'JEUDI', 'VENDREDI', 'SAMEDI'] as const;

export type PublicPlanningSession = {
  id: string;
  jourSemaine: string;
  idCohorte?: string | null;
  vacation?: {
    libelle?: string;
    heureDebut?: string;
    heureFin?: string;
    code?: string;
  } | null;
  salle?: { libelle?: string; code?: string; capacite?: number } | null;
  cohorte?: { id?: string; code?: string; libelle?: string } | null;
};

export type PublicCohorteRow = {
  id: string;
  code: string;
  libelle?: string;
  dateDebut?: string;
  dateFin?: string;
  statut?: string;
  capaciteMax?: number;
  capacity?: {
    inscrits?: number;
    capaciteEffective?: number;
    placesRestantes?: number;
    estComplete?: boolean;
  };
};

export function formatHeurePlanning(value?: string | null): string {
  if (!value) return '';
  const [h, m] = value.split(':');
  if (!h) return value;
  return `${h}h${(m ?? '00').padStart(2, '0')}`;
}

export function vacationHoraireLabel(session: PublicPlanningSession): string {
  const v = session.vacation;
  if (!v) return 'Vacation';
  if (v.heureDebut && v.heureFin) {
    return `${formatHeurePlanning(v.heureDebut)} – ${formatHeurePlanning(v.heureFin)}`;
  }
  return v.libelle ?? 'Vacation';
}

export function groupPlanningByDay(sessions: PublicPlanningSession[]) {
  const map = new Map<string, PublicPlanningSession[]>();
  for (const s of sessions) {
    const list = map.get(s.jourSemaine) ?? [];
    list.push(s);
    map.set(s.jourSemaine, list);
  }
  return JOUR_ORDER.filter((j) => map.has(j)).map((jour) => ({
    jour,
    label: JOUR_SEMAINE_LABEL[jour] ?? jour,
    sessions: (map.get(jour) ?? []).sort((a, b) => {
      const oa = a.vacation?.code ?? '';
      const ob = b.vacation?.code ?? '';
      return oa.localeCompare(ob);
    }),
  }));
}

export function formatDateFr(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}

/** Filtre les cohortes par id filière (champ API). */
export function filterCohortesByFiliereId(
  filiereId: string,
  cohortes: Array<PublicCohorteRow & { idFiliere?: string }>,
) {
  return cohortes.filter((c) => c.idFiliere === filiereId);
}

/**
 * Cohorte de référence pour afficher un rythme réaliste (séances/sem) :
 * priorité aux cohortes encore ouvertes avec places, sinon la plus proche.
 */
export function pickReferenceCohorte(cohortes: PublicCohorteRow[]): PublicCohorteRow | null {
  if (cohortes.length === 0) return null;
  const withPlaces = cohortes.filter((c) => !isCohorteFull(c));
  const pool = withPlaces.length > 0 ? withPlaces : cohortes;
  const sorted = [...pool].sort((a, b) => {
    const da = a.dateDebut ? new Date(a.dateDebut).getTime() : Number.POSITIVE_INFINITY;
    const db = b.dateDebut ? new Date(b.dateDebut).getTime() : Number.POSITIVE_INFINITY;
    return da - db;
  });
  return sorted[0] ?? null;
}

export function filterPlanningByCohorteId(
  sessions: PublicPlanningSession[],
  cohorteId: string,
): PublicPlanningSession[] {
  return sessions.filter((s) => s.idCohorte === cohorteId || s.cohorte?.id === cohorteId);
}

export function summarizePlanningDays(sessions: PublicPlanningSession[]): string {
  const jours = new Set(sessions.map((s) => s.jourSemaine));
  const labels = JOUR_ORDER.filter((j) => jours.has(j)).map((j) => JOUR_SEMAINE_LABEL[j] ?? j);
  if (labels.length === 0) return '';
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} et ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')} et ${labels[labels.length - 1]}`;
}

function uniqueVacationHoraires(sessions: PublicPlanningSession[]): string[] {
  const seen = new Set<string>();
  const rows: { sort: string; label: string }[] = [];
  for (const s of sessions) {
    const key = s.vacation?.code ?? vacationHoraireLabel(s);
    if (seen.has(key)) continue;
    seen.add(key);
    rows.push({
      sort: s.vacation?.heureDebut ?? key,
      label: vacationHoraireLabel(s),
    });
  }
  return rows.sort((a, b) => a.sort.localeCompare(b.sort)).map((r) => r.label);
}

function cohorteDurationMonths(cohortes: PublicCohorteRow[]): number | null {
  const withDates = cohortes.find((c) => c.dateDebut && c.dateFin);
  if (!withDates?.dateDebut || !withDates.dateFin) return null;
  const start = new Date(withDates.dateDebut);
  const end = new Date(withDates.dateFin);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  const days = Math.max(0, (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  const months = Math.round(days / 30);
  return months >= 1 ? months : null;
}

/**
 * Résumé organisation : durée cohorte · séances/semaine · horaires exacts des vacations.
 */
export function buildFiliereOrganisationSummary(
  planningSessions: PublicPlanningSession[],
  cohortes: PublicCohorteRow[],
  options?: { loading?: boolean },
): string {
  if (options?.loading) return 'Chargement des créneaux…';

  const months = cohorteDurationMonths(cohortes);
  const durationPart = months ? `Cohortes de ${months} mois` : 'Cohortes de 2 mois';

  const seancesCount = planningSessions.length;
  const seancesPart =
    seancesCount > 0
      ? `${seancesCount} séance${seancesCount > 1 ? 's' : ''} par semaine`
      : '3 séances par semaine';

  const horaires = uniqueVacationHoraires(planningSessions);
  if (horaires.length === 0) {
    return `${durationPart} · ${seancesPart}.`;
  }

  return `${durationPart} · ${seancesPart} · vacations de ${horaires.join(', ')}.`;
}

export function cohortePlacesLabel(cohorte: PublicCohorteRow): string {
  const cap = cohorte.capacity?.capaciteEffective ?? cohorte.capaciteMax ?? 0;
  const inscrits = cohorte.capacity?.inscrits ?? 0;
  if (cap <= 0) return 'Capacité non définie';
  if (isCohorteFull(cohorte)) {
    return `Plus de places disponibles (${inscrits}/${cap})`;
  }
  const rest = cohorte.capacity?.placesRestantes;
  if (rest != null) {
    return `${rest} place${rest > 1 ? 's' : ''} disponible${rest > 1 ? 's' : ''} (${inscrits}/${cap})`;
  }
  return `${inscrits}/${cap} inscrits`;
}
